import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const FormulariosIndependientes = () => {
  const { user, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formularios, setFormularios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newFormData, setNewFormData] = useState({
    titulo: '',
    descripcion: ''
  });

  useEffect(() => {
    loadFormularios();
  }, []);

  const loadFormularios = async () => {
    try {
      const response = await axios.get(`${API}/formularios/independientes`);
      setFormularios(response.data);
    } catch (error) {
      toast.error('Error al cargar formularios independientes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFormulario = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/formularios`, newFormData);
      toast.success('Formulario independiente creado exitosamente');
      setIsCreateDialogOpen(false);
      setNewFormData({ titulo: '', descripcion: '' });
      loadFormularios();
    } catch (error) {
      toast.error('Error al crear formulario');
    }
  };

  const handleBorrarFormulario = async (formularioId) => {
    if (!window.confirm('¿Estás seguro de que quieres borrar este formulario?')) {
      return;
    }

    try {
      await axios.delete(`${API}/formularios/${formularioId}`);
      toast.success('Formulario borrado exitosamente');
      loadFormularios();
    } catch (error) {
      toast.error('Error al borrar formulario');
    }
  };

  const handleTogglePublico = async (formularioId) => {
    try {
      const response = await axios.put(`${API}/formularios/${formularioId}/toggle-publico`);
      const { es_publico, link_publico } = response.data;
      
      if (es_publico) {
        toast.success('Formulario ahora es público. Puedes compartir el link.');
      } else {
        toast.success('Acceso público desactivado');
      }
      
      loadFormularios();
    } catch (error) {
      toast.error('Error al cambiar configuración pública');
    }
  };

  const handleCopyLink = (linkPublico) => {
    const fullUrl = `${window.location.origin}/formulario-publico/${linkPublico}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('Link copiado al portapapeles');
  };

  const formatFecha = (fecha) => {
    if (!fecha) return 'Sin fecha';
    try {
      return new Date(fecha).toLocaleDateString('es-UY', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <i className="fas fa-spinner fa-spin text-4xl text-blue-600"></i>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => window.history.length > 2 ? window.history.back() : window.location.href = '/app/dashboard'}
            className="text-gray-600 hover:text-gray-800"
            data-testid="back-button"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Volver
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              📝 Formularios Independientes
            </h1>
            <p className="text-gray-600">
              Gestiona formularios de evaluación independientes no vinculados a clases específicas
            </p>
          </div>
        </div>

        {user.rol === 'admin' && (
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="instituto-gradient text-white">
                <i className="fas fa-plus mr-2"></i>
                Nuevo Formulario
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Crear Formulario Independiente</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateFormulario} className="space-y-4">
                <div>
                  <Label htmlFor="titulo">Título del Formulario *</Label>
                  <Input
                    id="titulo"
                    value={newFormData.titulo}
                    onChange={(e) => setNewFormData({ ...newFormData, titulo: e.target.value })}
                    placeholder="Ej: Evaluación de Visitas a Iglesias"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="descripcion">Descripción</Label>
                  <Input
                    id="descripcion"
                    value={newFormData.descripcion}
                    onChange={(e) => setNewFormData({ ...newFormData, descripcion: e.target.value })}
                    placeholder="Descripción opcional del formulario"
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="instituto-gradient text-white">
                    Crear Formulario
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Formularios List */}
      <Card className="shadow-lg">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
          <CardTitle className="flex items-center text-purple-800">
            <i className="fas fa-file-alt mr-3 text-2xl"></i>
            Formularios Disponibles
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {formularios.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <i className="fas fa-inbox text-4xl mb-3"></i>
              <p className="text-lg">No hay formularios independientes creados</p>
              {user.rol === 'admin' && (
                <p className="text-sm mt-2">Haz clic en "Nuevo Formulario" para crear uno</p>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {formularios.map((formulario) => (
                <div key={formulario.id} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="bg-purple-100 rounded-full p-2 flex-shrink-0">
                          <i className="fas fa-file-alt text-purple-600 text-lg"></i>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-semibold text-gray-900 truncate">{formulario.titulo}</h4>
                          <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                            <span className="flex items-center">
                              <i className="fas fa-calendar mr-1"></i>
                              {formatFecha(formulario.fecha_creacion)}
                            </span>
                            <span className="flex items-center">
                              <i className="fas fa-user mr-1"></i>
                              {formulario.profesor_nombre || 'Sin asignar'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center flex-wrap gap-2 ml-0 sm:ml-12">
                        <Badge variant="outline" className={`text-xs ${
                          formulario.activo 
                            ? 'bg-green-50 text-green-700 border-green-300' 
                            : 'bg-red-50 text-red-700 border-red-300'
                        }`}>
                          <i className={`fas ${formulario.activo ? 'fa-check-circle' : 'fa-times-circle'} mr-1`}></i>
                          {formulario.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                        <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700">
                          <i className="fas fa-layer-group mr-1"></i>
                          Formulario Independiente
                        </Badge>
                        {formulario.es_publico && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-300">
                            <i className="fas fa-globe mr-1"></i>
                            Link Público
                          </Badge>
                        )}
                      </div>

                      {/* Link público - mostrar si está activo */}
                      {formulario.es_publico && formulario.link_publico && user.rol === 'admin' && (
                        <div className="mt-3 ml-0 sm:ml-12 flex items-center gap-2 bg-blue-50 p-2 rounded-lg">
                          <i className="fas fa-link text-blue-600"></i>
                          <code className="text-xs text-blue-800 bg-blue-100 px-2 py-1 rounded truncate max-w-[200px] sm:max-w-[400px]">
                            {window.location.origin}/formulario-publico/{formulario.link_publico}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopyLink(formulario.link_publico)}
                            className="text-blue-600 hover:bg-blue-100 h-7 px-2"
                          >
                            <i className="fas fa-copy"></i>
                          </Button>
                        </div>
                      )}
                      
                      {formulario.descripcion && (
                        <div className="mt-3 ml-0 sm:ml-12 text-sm text-gray-600">
                          <i className="fas fa-info-circle mr-2"></i>
                          {formulario.descripcion}
                        </div>
                      )}
                    </div>

                    {/* Botones de acción - Responsive */}
                    <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:flex-shrink-0">
                      {/* SOLO ADMIN MASTER: puede gestionar, ver respuestas y borrar */}
                      {user.rol === 'admin' && user.email === 'ibauruguay01@gmail.com' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleTogglePublico(formulario.id)}
                            className={`flex-1 sm:flex-none ${
                              formulario.es_publico 
                                ? 'text-orange-600 border-orange-200 hover:bg-orange-50' 
                                : 'text-blue-600 border-blue-200 hover:bg-blue-50'
                            }`}
                            title={formulario.es_publico ? 'Desactivar link público' : 'Activar link público'}
                          >
                            <i className={`fas ${formulario.es_publico ? 'fa-lock' : 'fa-globe'} mr-2`}></i>
                            <span className="hidden sm:inline">{formulario.es_publico ? 'Privado' : 'Público'}</span>
                          </Button>

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/app/formulario/${formulario.id}/gestionar`)}
                            className="text-purple-600 border-purple-200 hover:bg-purple-50 flex-1 sm:flex-none"
                          >
                            <i className="fas fa-cog mr-2"></i>
                            <span className="hidden sm:inline">Gestionar</span>
                            <span className="sm:hidden">Editar</span>
                          </Button>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/app/formulario/${formulario.id}/respuestas`)}
                            className="text-blue-600 border-blue-200 hover:bg-blue-50 flex-1 sm:flex-none"
                          >
                            <i className="fas fa-chart-bar mr-2"></i>
                            Respuestas
                          </Button>

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleBorrarFormulario(formulario.id)}
                            className="text-red-600 border-red-200 hover:bg-red-50 flex-1 sm:flex-none"
                          >
                            <i className="fas fa-trash mr-2"></i>
                            <span className="hidden sm:inline">Borrar</span>
                            <span className="sm:hidden">
                              <i className="fas fa-trash"></i>
                            </span>
                          </Button>
                        </>
                      )}
                      
                      {/* PROFESORES: pueden responder Y ver respuestas */}
                      {user.rol === 'profesor' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/app/formulario/${formulario.id}/respuestas`)}
                            className="text-blue-600 border-blue-200 hover:bg-blue-50 flex-1 sm:flex-none"
                          >
                            <i className="fas fa-chart-bar mr-2"></i>
                            Ver Respuestas
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/app/formulario/${formulario.id}/responder`)}
                            className="text-green-600 border-green-200 hover:bg-green-50 flex-1 sm:flex-none"
                          >
                            <i className="fas fa-edit mr-2"></i>
                            Responder
                          </Button>
                        </>
                      )}
                      
                      {/* OTROS ADMINS (no master): solo ver respuestas */}
                      {user.rol === 'admin' && user.email !== 'ibauruguay01@gmail.com' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/app/formulario/${formulario.id}/respuestas`)}
                          className="text-blue-600 border-blue-200 hover:bg-blue-50 w-full sm:w-auto"
                        >
                          <i className="fas fa-chart-bar mr-2"></i>
                          Ver Respuestas
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FormulariosIndependientes;
